'use client'

import { CollectionContainer } from '@/components/common/collection/collection-container'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useState } from 'react'

interface OutfitHighlight {
  image: string
  key: string
}

export const UVOutfitHighlights = () => {
  const isMobile = useIsMobileClient()
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null)
  const t = useTranslations()

  // Auto play effect
  useEffect(() => {
    if (!carouselApi || outfitHighlights.length < 2) return
    const interval = setInterval(() => {
      if (carouselApi) {
        carouselApi.scrollNext()
      }
    }, 3000) // 3 seconds
    return () => clearInterval(interval)
  }, [carouselApi])

  return (
    <CollectionContainer>
      <Carousel
        opts={{
          align: 'start',
          loop: true,
          skipSnaps: false,
          slidesToScroll: 1,
        }}
        setApi={setCarouselApi}
        className='w-full'
      >
        <CarouselContent className='-ml-4'>
          {outfitHighlights.map((outfitHighlight, index) => (
            <CarouselItem
              key={index}
              className={cn(
                'group cursor-pointer pl-4',
                isMobile ? 'basis-[66.666%]' : 'basis-1/4'
              )}
            >
              <div className='relative overflow-hidden rounded-lg'>
                <div className='aspect-[434/581]'>
                  <Image
                    src={ImageHelper.getImageUrlCdn(outfitHighlight.image)}
                    alt={t(
                      `uv_protection_hardcode.outfit_highlights.${outfitHighlight.key}.title`
                    )}
                    fill
                    unoptimized
                    className='h-full w-full object-cover transition-all duration-500 ease-in-out group-hover:scale-105'
                  />
                </div>
                <div className='absolute bottom-0 left-0 flex h-1/2 w-full flex-col justify-end bg-gradient-to-t from-neutral-800 to-transparent p-4 pb-3 text-white md:pb-6'>
                  <h3 className='text-lg font-medium uppercase text-white 2xl:text-2xl'>
                    {t(
                      `uv_protection_hardcode.outfit_highlights.${outfitHighlight.key}.title`
                    )}
                  </h3>
                  <p className='text-xs text-white opacity-90 md:text-base'>
                    {t(
                      `uv_protection_hardcode.outfit_highlights.${outfitHighlight.key}.description`
                    )}
                  </p>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselNavigation />
      </Carousel>
    </CollectionContainer>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-3 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-black p-2 text-white md:-left-5 md:p-3'
        variant='outline'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-3 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-black p-2 text-white md:-right-5 md:p-3'
        variant='outline'
        hideIfEnd
      />
    </>
  )
}

const outfitHighlights: OutfitHighlight[] = [
  {
    image: '/image/2026/06/01/6._Certificate.jpg',
    key: 'certificate',
  },
  {
    image: '/image/2026/07/01/CHONG_NANG_UPF50_.jpg',
    key: 'upf50',
  },
  {
    image: '/image/2026/07/01/lam_mat_de_chiu.jpg',
    key: 'cool_comfort',
  },
  {
    image: '/image/2026/07/01/thoang_khi_kho_nhanh.jpg',
    key: 'breathable_quick_dry',
  },
  {
    image: '/image/2026/07/01/THIET_KE_ALL-IN-ONE_.jpg',
    key: 'all_in_one',
  },
  {
    image: '/image/2026/07/01/co_dan_linh_hoat.jpg',
    key: 'flexible_stretch',
  },
  {
    image: '/image/2026/07/01/phong_cach_toi_gian.jpg',
    key: 'minimal_style',
  },
  {
    image: '/image/2026/07/01/cho_moi_hanh_trinh.jpg',
    key: 'every_journey',
  },
]
